/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { useComputed } from 'src/core/hooks/useComputed';
import { FeatureWithModels } from 'src/luis/modules/features/types/FeatureWithModels';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { flattenNDepthChildren } from 'src/luis/projects/entities/utils/simpleEntityHelpers/simpleEntityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { Resource } from 'src/luis/types/Resource';

/**
 * Gets the models that uses the given feature as a feature.
 *
 * @param description The feature to search for.
 * @param intents All the available intents of the application to search with.
 * @param entities All the available entities of the application to search with.
 * @returns The models that use the given feature.
 */
export const useFeatureUsedByModels = <T extends Resource>(features: T[]): FeatureWithModels<T>[] => {
    const { intents } = useIntents();
    const { entities } = useEntities();

    return useComputed(
        () =>
            features.map(feature => {
                const filteredIntents = intents.filter(i => i.features.find(f => f.id === feature.id));
                const simpleEntities = flattenNDepthChildren(
                    <SimpleEntity[]>entities.filter(e => e.typeId === ModelType.Simple)
                ).filter(e => e.features.some(d => d.id === feature.id));

                const compositeEntities = entities
                    .filter(e => e.typeId === ModelType.Composite)
                    .filter((e: CompositeEntity) => e.features.some(d => d.id === feature.id));

                return { ...feature, usedBy: [...simpleEntities, ...filteredIntents, ...compositeEntities] };
            }),
        [intents, entities, features]
    );
};
